/**
 * MainLayout Component
 * Sidebar layout with navigation, profile & language at bottom, and main content area
 */
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

//sonarqube
import PropTypes from "prop-types";

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsIcon from "@mui/icons-material/Settings";
import Logout from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Hooks
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useRole } from "../../security";
import { useIdleTimeout, useLanguageDirection } from "../../hooks";
import { useUIStore } from "../../store";
import { GROUPS, PERMISSIONS, ROUTES } from "../../routes/constants";
import { PageLoader } from "../ui/LoadingSpinner";
import SessionWarning from "../ui/SessionWarning";
import LanguageSwitcher from "../ui/Language/LanguageSwitcher";
// Styles
import styles from "./MainLayout.styles";

const MainLayout = ({ children }) => {
  const { t, i18n } = useTranslation("layoutTranslation");
  const { dir } = useLanguageDirection();
  const isRtl = dir === "rtl";
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const language = useUIStore((state) => state.language); // persisted language
  const setLanguage = useUIStore((state) => state.setLanguage);

  // Initialize collapsed state based on screen size
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isLoading } = useAuth();
  const { hasPermission, belongsToGroup } = useRole();

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const { showWarning, timeUntilTimeout, extendSession } = useIdleTimeout();

  // Sync collapsed state if screen size changes (optional but nice)
  const sidebarCollapsed = useUIStore((state) => state.layout.sidebarCollapsed);
  const setSidebarCollapsed = useUIStore((state) => state.setSidebarCollapsed);

  const handleSessionWarningLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language); // ensure i18n uses persisted language
    }
  }, [language, i18n]);

  //auto collapse sidebar for small screens
  useEffect(() => {
    if (isSmallScreen && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  }, [isSmallScreen, sidebarCollapsed, setSidebarCollapsed]);
  // Navigation items
  const navigationItems = [
    // {
    //   text: t("navigation.home", "Home"),
    //   path: "/home",
    //   icon: <img src="/MainlayoutIcons/homeIcon.svg" alt="homeIcon" />,
    //   visible: true,
    // },
    {
      text: t("navigation.projects", "Projects"),
      path: "/projects",
      icon: (
        <img src="/MainlayoutIcons/projectsPageIcon.svg" alt="projectsIcon" />
      ),
      visible: true,
    },
    {
      text: t("navigation.users", "Users"),
      icon: <img src="/MainlayoutIcons/usersPageIcon.svg" alt="usersIcon" />,
      path: ROUTES.USERS,
      visible:
        belongsToGroup(GROUPS.ADMINS) ||
        hasPermission(PERMISSIONS.CAN_MANAGE_USERS),
    },
    {
      text: t("navigation.dashboard", "Dashboard"),
      icon: <DashboardIcon />,
      path: ROUTES.DASHBOARD,
      visible: true,
    },
    {
      text: t("navigation.profile", "Profile"),
      icon: <PersonIcon />,
      path: ROUTES.PROFILE,
      visible: true,
    },
    {
      text: t("navigation.admin", "Admin"),
      icon: <AdminPanelSettingsIcon />,
      path: ROUTES.ADMIN,
      visible: false,
    },
  ];

  const visibleNavItems = navigationItems.filter((item) => item.visible);

  // Handlers
  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleUserMenuOpen = (e) => setUserMenuAnchor(e.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleLangMenuClose = () => setLangMenuAnchor(null);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    handleLangMenuClose();
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate(ROUTES.LOGIN, { replace: true });
    } finally {
      setLoggingOut(false);
    }
    handleUserMenuClose();
  };

  const handleToggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  if (loggingOut) {
    return <PageLoader message="Logging out..." />;
  }

  const isActiveRoute = (path) => location.pathname === path;

  const getChevronIcon = () => {
    if (isRtl) {
      return sidebarCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />;
    } else {
      return sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />;
    }
  };

  const drawerContent = (
    <Box sx={styles.drawerContent(sidebarCollapsed)}>
      <Box sx={styles.logoContainer(sidebarCollapsed)}>
        <Box
          component="img"
          alt="logo"
          src={
            sidebarCollapsed
              ? "/JuzoorLogo-small.svg"
              : "/JuzoorLogo-TransparentBG.svg"
          }
          sx={styles.logo(sidebarCollapsed)}
        />
      </Box>
      <Box sx={styles.sideBarWrapper}>
        <List sx={styles.navList}>
          {visibleNavItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <Tooltip
                title={sidebarCollapsed ? item.text : ""}
                placement={isRtl ? "left" : "right"}
                arrow
              >
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  selected={isActiveRoute(item.path)}
                  sx={styles.navButton(sidebarCollapsed)}
                >
                  <ListItemIcon sx={styles.navIcon(sidebarCollapsed)}>
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={item.text} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        <Divider />

        <Box sx={styles.bottomActions(sidebarCollapsed)}>
          <Tooltip
            title={
              sidebarCollapsed
                ? user?.full_name || user?.name || user?.email || "User"
                : ""
            }
            placement={isRtl ? "left" : "right"}
            arrow
          >
            <Box
              sx={styles.userInfo(sidebarCollapsed)}
              onClick={handleUserMenuOpen}
            >
              <Avatar
                sx={{
                  ...styles.avatar,
                  ...(sidebarCollapsed ? {} : { mr: 1 }),
                }}
              >
                {user?.full_name?.charAt(0) ??
                  user?.name?.charAt(0) ??
                  user?.email?.charAt(0) ??
                  "U"}
              </Avatar>
              {!sidebarCollapsed && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ ...styles.userName, ml: 1 }}
                >
                  {user?.full_name || user?.name || user?.email || "User"}
                </Typography>
              )}
            </Box>
          </Tooltip>

          <LanguageSwitcher collapsed={sidebarCollapsed} />
        </Box>

        {!sidebarCollapsed && (
          <Box sx={styles.versionBox}>
            <Typography variant="caption" sx={styles.versionText}>
              {t("app.version", "v1.0")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={styles.root(sidebarCollapsed)}>
      <Box component="nav" sx={styles.nav(sidebarCollapsed)}>
        {showWarning && (
          <SessionWarning
            open={showWarning}
            timeLeft={timeUntilTimeout}
            onExtendSession={extendSession}
            onLogout={handleSessionWarningLogout}
          />
        )}

        <Box sx={styles.drawerWrapper(sidebarCollapsed, false, isRtl)}>
          <Drawer
            variant="permanent"
            anchor={isRtl ? "right" : "left"}
            open
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": styles.drawerPaper(sidebarCollapsed, false),
            }}
          >
            {drawerContent}
          </Drawer>

          {/* Show collapse button on all screens (optional: remove !isSmallScreen if you want it on mobile too) */}
          <IconButton
            onClick={handleToggleCollapse}
            sx={styles.collapseButtonOverlay(sidebarCollapsed, isRtl)}
            aria-label={
              sidebarCollapsed ? "expand sidebar" : "collapse sidebar"
            }
          >
            {getChevronIcon()}
          </IconButton>
        </Box>
      </Box>

      <Box component="main" sx={styles.content(sidebarCollapsed)}>
        {children}
      </Box>

      {/* Menus */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleNavigate(ROUTES.PROFILE);
            handleUserMenuClose();
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {t("navigation.profile")}
        </MenuItem>
        {/*
        <MenuItem onClick={handleUserMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          {t("navigation.settings")}
        </MenuItem>
        */}
        <Divider />
        <MenuItem onClick={handleLogout} disabled={isLoading}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t("common.buttons.logout")}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={langMenuAnchor}
        open={Boolean(langMenuAnchor)}
        onClose={handleLangMenuClose}
      >
        <MenuItem onClick={() => handleLanguageChange("en")}>
          {t("common.language.english", "English")}
        </MenuItem>
        <MenuItem onClick={() => handleLanguageChange("ar")}>
          {t("common.language.arabic", "Arabic")}
        </MenuItem>
      </Menu>
    </Box>
  );
};

//sonarqube
MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MainLayout;
